import { capitalize } from 'es-toolkit';
import { Form, Formik, FormikProps } from 'formik';
import React, { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';

import TextField from '@components/Form/TextField';
import FirstPartyDataFields from '@components/UserOnboardingModal/ProfileFields/FirstPartyDataFields';
import SubIndustryList from '@components/UserOnboardingModal/ProfileFields/SubIndustryList';
import SubJobFunctionList from '@components/UserOnboardingModal/ProfileFields/SubJobFunctionList';
import { useProfile } from '@hooks';
import { User } from '@utils/backend-api/currentUser';
import {
  COMPANY_SIZE_OPTIONS,
  JOB_FUNCTION_OPTIONS,
  JOB_LEVEL_OPTIONS,
  ORGANIZATION_INDUSTRY_OPTIONS,
  PROFILE_FIELD_OTHER_VALUE,
  PROFILE_OTHER_OPTION,
  SanitizedProfileSettingsParams,
} from '@utils/backend-api/profileSettings';
import { SubscribeSource } from '@utils/backend-api/userOnboarding';
import { FirstPartyDataProps } from '@utils/backend-api/userProfile';
import { BLANK_OPTION, BLANK_RECORD_OPTION } from '@utils/constants';
import { AutocompleteRecord } from '@utils/interfaces';

import { getFieldsForSource, getProfileSchema } from './schema';

const INITIAL_STATE = {
  firstName: '',
  lastName: '',
  company: BLANK_RECORD_OPTION,
  jobLevel: BLANK_OPTION,
  jobFunction: BLANK_OPTION,
  subJobFunction: BLANK_OPTION,
  industry: BLANK_OPTION,
  subIndustry: BLANK_OPTION,
  companySize: BLANK_OPTION,
  postalCode: '',
  customIndustry: BLANK_RECORD_OPTION,
  customJobFunction: BLANK_RECORD_OPTION,
  customJobLevel: BLANK_RECORD_OPTION,
  customSubIndustry: BLANK_RECORD_OPTION,
  customSubJobFunction: BLANK_RECORD_OPTION,
};

interface StepProfileProps {
  user: User;
  saveProfile: (
    values: SanitizedProfileSettingsParams,
    options?: { onSuccess?: () => void }
  ) => void;
  onStepComplete: () => void;
  stepSaveRef: MutableRefObject<(() => void | Promise<void>) | null>;
  subscribeSource?: SubscribeSource;
}

export interface StepProfileFormProps extends FirstPartyDataProps {
  company: AutocompleteRecord;
  firstName: string;
  jobTitle?: AutocompleteRecord;
  lastName: string;
}

const StepProfile = ({
  user,
  saveProfile,
  onStepComplete,
  stepSaveRef,
  subscribeSource,
}: StepProfileProps) => {
  const [initialValues, setInitialValues] = useState(INITIAL_STATE);
  const formikRef = useRef<FormikProps<Record<string, unknown>> | null>(null);
  const { profile, isLoadingProfile } = useProfile();

  // Determine if this is an interrupted onboarding (paywall flow)
  const isInterruptedOnboarding = subscribeSource === SubscribeSource.PAYWALL;

  // Create memoized schema based on subscribeSource
  const validationSchema = useMemo(() => getProfileSchema(subscribeSource), [subscribeSource]);

  // Create memoized data fields based on subscribeSource
  // Order: jobLevel, jobFunction, companyName, subJobFunction, then conditional fields
  const dataFields = useMemo(() => {
    const schemaFields = getFieldsForSource(subscribeSource);
    const fields: string[] = [];

    // First add jobLevel and jobFunction (always present)
    if (schemaFields.includes('jobLevel')) {
      fields.push('jobLevel');
    }
    if (schemaFields.includes('jobFunction')) {
      fields.push('jobFunction');
    }

    // Add subJobFunction (always present)
    if (schemaFields.includes('subJobFunction')) {
      fields.push('subJobFunction');
    }

    // Add conditional fields (only for promo flow)
    if (schemaFields.includes('industry')) {
      fields.push('organizationIndustry');
    }
    if (schemaFields.includes('subIndustry')) {
      fields.push('organizationSubIndustry');
    }

    fields.push('companyName');

    if (schemaFields.includes('companySize')) {
      fields.push('companySize');
    }
    if (schemaFields.includes('postalCode')) {
      fields.push('postalCode');
    }

    return fields;
  }, [subscribeSource]);

  useEffect(() => {
    const getSelectedOptions = (possibleOptions, profileKey) => {
      const selectedStandardValue = profile?.[profileKey];
      const standardOption =
        possibleOptions?.find((item) => item.value === selectedStandardValue) || BLANK_OPTION;
      const customOption = BLANK_RECORD_OPTION;

      if (selectedStandardValue && !standardOption.label) {
        return {
          standardOption: PROFILE_OTHER_OPTION,
          customOption: { label: capitalize(selectedStandardValue), value: selectedStandardValue },
        };
      }

      return { standardOption, customOption };
    };

    if (!isLoadingProfile && profile) {
      const companySize =
        COMPANY_SIZE_OPTIONS.find((item) => item.value === profile?.companySize) || BLANK_OPTION;

      const industryValueAndCustomValue = getSelectedOptions(
        ORGANIZATION_INDUSTRY_OPTIONS,
        'organizationIndustry'
      );
      const jobFunctionValueAndCustomValue = getSelectedOptions(
        JOB_FUNCTION_OPTIONS,
        'jobFunction'
      );
      const jobLevelValueAndCustomValue = getSelectedOptions(JOB_LEVEL_OPTIONS, 'jobLevel');
      const jobFunction = JOB_FUNCTION_OPTIONS.find((item) => item.value === profile?.jobFunction);
      const industry = ORGANIZATION_INDUSTRY_OPTIONS.find(
        (item) => item.value === profile?.organizationIndustry
      );
      const subJobFunctionValueAndCustomValue = getSelectedOptions(
        SubJobFunctionList[jobFunction?.label],
        'subJobFunction'
      );
      const subIndustryValueAndCustomValue = getSelectedOptions(
        SubIndustryList[industry?.label],
        'organizationSubIndustry'
      );

      setInitialValues({
        ...INITIAL_STATE,
        firstName: profile.name?.split(' ')[0] || user.name?.split(' ')[0] || '',
        lastName: profile.name?.split(' ').slice(1).join(' ') || user.name?.split(' ')[1] || '',
        company: profile.company
          ? { label: capitalize(profile.company.label), value: profile.company.value }
          : BLANK_RECORD_OPTION,
        companySize: companySize,
        customIndustry: industryValueAndCustomValue.customOption,
        customJobFunction: jobFunctionValueAndCustomValue.customOption,
        customJobLevel: jobLevelValueAndCustomValue.customOption,
        customSubIndustry: subIndustryValueAndCustomValue.customOption,
        customSubJobFunction: subJobFunctionValueAndCustomValue.customOption,
        industry: industryValueAndCustomValue.standardOption,
        jobFunction: jobFunctionValueAndCustomValue.standardOption,
        jobLevel: jobLevelValueAndCustomValue.standardOption,
        postalCode: profile.postalCode || '',
        subIndustry: subIndustryValueAndCustomValue.standardOption,
        subJobFunction: subJobFunctionValueAndCustomValue.standardOption,
      });
    } else if (!isLoadingProfile) {
      // Fallback to user name if profile is not available
      setInitialValues({
        ...INITIAL_STATE,
        firstName: user.name?.split(' ')[0] || '',
        lastName: user.name?.split(' ')[1] || '',
      });
    }
  }, [user, profile, isLoadingProfile]);

  useEffect(() => {
    stepSaveRef.current = async () => {
      const formik = formikRef.current;
      if (!formik) return;

      const errors = await formik.validateForm();

      if (Object.keys(errors).length > 0) {
        // Touch all fields to display validation errors
        const touchedFields = Object.keys(formik.values).reduce(
          (acc, key) => ({ ...acc, [key]: true }),
          {}
        );
        formik.setTouched(touchedFields);
        throw new Error('Please fill in all required fields');
      }

      // If validation passes, submit the form
      await formik.submitForm();
    };
    return () => {
      stepSaveRef.current = null;
    };
  }, [stepSaveRef]);

  const buildSanitizedParams = (
    values: Record<string, unknown>
  ): SanitizedProfileSettingsParams & Record<string, unknown> => ({
    name: `${values.firstName ?? ''} ${values.lastName ?? ''}`.trim() || undefined,
    company_name: (values.company as { label?: string })?.label || undefined,
    job_level:
      (values.jobLevel as { value?: string })?.value === PROFILE_FIELD_OTHER_VALUE
        ? (values.customJobLevel as { label?: string })?.label
        : (values.jobLevel as { value?: string })?.value,
    job_function:
      (values.jobFunction as { value?: string })?.value === PROFILE_FIELD_OTHER_VALUE
        ? (values.customJobFunction as { label?: string })?.label
        : (values.jobFunction as { value?: string })?.value,
    sub_job_function:
      (values.subJobFunction as { value?: string })?.value === PROFILE_FIELD_OTHER_VALUE
        ? (values.customSubJobFunction as { label?: string })?.label
        : (values.subJobFunction as { value?: string })?.value,
    organization_industry:
      (values.industry as { value?: string })?.value === PROFILE_FIELD_OTHER_VALUE
        ? (values.customIndustry as { label?: string })?.label
        : (values.industry as { value?: string })?.value,
    organization_sub_industry:
      (values.subIndustry as { value?: string })?.value === PROFILE_FIELD_OTHER_VALUE
        ? (values.customSubIndustry as { label?: string })?.label
        : (values.subIndustry as { value?: string })?.value,
    company_size: (values.companySize as { value?: string })?.value,
    postal_code: values.postalCode as string,
    profile_enabled: true,
  });

  const handleSubmit = (values: Record<string, unknown>) => {
    const params = buildSanitizedParams(values);
    saveProfile(params as SanitizedProfileSettingsParams, { onSuccess: onStepComplete });
  };

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {() => (
        <Form id="onboarding-profile" className="form-container w-[340px] pb-24">
          <div className="block font-suisse-works text-subheadline-md md:hidden">
            Welcome to <span className="font-bold">The Information</span>
            <p className="mb-24 mt-0 font-suisse-works text-byline-md">
              You now have insider access to the biggest stories in Silicon Valley before anyone
              else.
            </p>
          </div>
          <div className="mb-24 flex gap-8">
            <TextField name="firstName" label="First Name" disabled />
            <TextField name="lastName" label="Last Name" disabled />
          </div>
          <FirstPartyDataFields
            dataFields={dataFields}
            isInterruptedOnboarding={isInterruptedOnboarding}
          />
        </Form>
      )}
    </Formik>
  );
};

export default StepProfile;
